import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateOrder,
  UpdateOrder,
  GetOrders,
  GetOrderById,
  CreateOrderReturn,
  DeleteOrder,
  GetOrderReturns,
  SendOrderInvoiceWhatsApp,
  GetPublicOrderById,
  GetOrderReturnById,
  GetPublicOrderReturnById,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const orderQueryKeys = {
  orders: (params?: Parameters<typeof GetOrders>[0]) =>
    ["orders", params] as const,
  orderById: (params?: Parameters<typeof GetOrderById>[0]) =>
    ["orderById", params] as const,
  orderReturns: (params?: Parameters<typeof GetOrderReturns>[0]) =>
    ["orderReturns", params] as const,
  publicOrderById: (tenantSlug: string, id: string) =>
    ["publicOrderById", { tenantSlug, id }] as const,
  orderReturnById: (params?: Parameters<typeof GetOrderReturnById>[0]) =>
    ["orderReturnById", params] as const,
  publicOrderReturnById: (tenantSlug: string, id: string) =>
    ["publicOrderReturnById", { tenantSlug, id }] as const,
};

export const invalidateOrderQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["orders"] });
  queryClient.invalidateQueries({ queryKey: ["orderById"] });
  queryClient.invalidateQueries({ queryKey: ["orderReturns"] });
};

export const useOrdersQuery = (
  params: Parameters<typeof GetOrders>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.orders(params),
    queryFn: async () => {
      const res = await GetOrders(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useOrdersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOrders>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: orderQueryKeys.orders(params),
        queryFn: async () => {
          const res = await GetOrders(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const useOrderByIdQuery = (
  params: Parameters<typeof GetOrderById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.orderById(params),
    queryFn: async () => unwrapApiResponse(await GetOrderById(params)),
  });
};

export const usePublicOrderByIdQuery = (
  tenantSlug: string,
  id: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.publicOrderById(tenantSlug, id),
    queryFn: async () =>
      unwrapApiResponse(await GetPublicOrderById(tenantSlug, id)),
    enabled: !!tenantSlug && !!id && options?.enabled !== false,
  });
};

export const useOrderReturnByIdQuery = (
  params: Parameters<typeof GetOrderReturnById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.orderReturnById(params),
    queryFn: async () => unwrapApiResponse(await GetOrderReturnById(params)),
    enabled: !!params && options?.enabled !== false,
  });
};

export const usePublicOrderReturnByIdQuery = (
  tenantSlug: string,
  id: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.publicOrderReturnById(tenantSlug, id),
    queryFn: async () =>
      unwrapApiResponse(await GetPublicOrderReturnById(tenantSlug, id)),
    enabled: !!tenantSlug && !!id && options?.enabled !== false,
  });
};

export const useOrderByIdRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOrderById>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: orderQueryKeys.orderById(params),
        queryFn: async () => unwrapApiResponse(await GetOrderById(params)),
      }),
  });
};

export const useOrderReturnsQuery = (
  params: Parameters<typeof GetOrderReturns>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: orderQueryKeys.orderReturns(params),
    queryFn: async () => {
      const res = await GetOrderReturns(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useOrderReturnsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetOrderReturns>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: orderQueryKeys.orderReturns(params),
        queryFn: async () => {
          const res = await GetOrderReturns(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const useCreateOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateOrder>[0]) =>
      CreateOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOrderQueries(queryClient);
      }
    },
  });
};

export const useUpdateOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateOrder>[0]) =>
      UpdateOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOrderQueries(queryClient);
      }
    },
  });
};

export const useCreateOrderReturnMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateOrderReturn>[0]) =>
      CreateOrderReturn(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOrderQueries(queryClient);
      }
    },
  });
};

export const useDeleteOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteOrder>[0]) =>
      DeleteOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateOrderQueries(queryClient);
      }
    },
  });
};

export const useSendOrderInvoiceWhatsAppMutation = () => {
  return useMutation({
    mutationFn: (payload: Parameters<typeof SendOrderInvoiceWhatsApp>[0]) =>
      SendOrderInvoiceWhatsApp(payload),
  });
};

export const useOrderService = (config?: {
  fetchOrdersParams?: Parameters<typeof GetOrders>[0];
  fetchOrdersEnabled?: boolean;
  fetchOrderByIdParams?: Parameters<typeof GetOrderById>[0];
  fetchOrderByIdEnabled?: boolean;
  fetchOrderReturnsParams?: Parameters<typeof GetOrderReturns>[0];
  fetchOrderReturnsEnabled?: boolean;
}) => {
  const ordersQuery = useOrdersQuery(config?.fetchOrdersParams, {
    enabled:
      config?.fetchOrdersEnabled !== false &&
      config?.fetchOrdersParams !== undefined &&
      config?.fetchOrdersParams !== null,
  });

  const orderByIdQuery = useOrderByIdQuery(config?.fetchOrderByIdParams, {
    enabled:
      config?.fetchOrderByIdEnabled !== false &&
      config?.fetchOrderByIdParams !== undefined &&
      config?.fetchOrderByIdParams !== null,
  });

  const orderReturnsQuery = useOrderReturnsQuery(
    config?.fetchOrderReturnsParams,
    {
      enabled:
        config?.fetchOrderReturnsEnabled !== false &&
        config?.fetchOrderReturnsParams !== undefined &&
        config?.fetchOrderReturnsParams !== null,
    },
  );

  const createOrderMutation = useCreateOrderMutation();
  const updateOrderMutation = useUpdateOrderMutation();
  const createOrderReturnMutation = useCreateOrderReturnMutation();
  const deleteOrderMutation = useDeleteOrderMutation();

  return {
    ordersQuery,
    orderByIdQuery,
    orderReturnsQuery,
    createOrderMutation,
    updateOrderMutation,
    createOrderReturnMutation,
    deleteOrderMutation,
  };
};
